// wafSafeApi.js
// ─────────────────────────────────────────────────────────────────────────────
// All dashboard API calls, made safe for the Front Door WAF.
//
// Why: dashboard specs carry SQL, JS-style expressions and LLM prose. The WAF's
// SQLi/XSS rules (942xxx / 941xxx) score those as attacks and block the request.
// Every request body that can contain such content is sent as an opaque
// base64 (optionally gzip) envelope:
//
//     POST body:  { "payload": "<base64>" }
//     header:     X-Payload-Encoding: b64 | gzip+b64
//
// Query params that can contain free text are sent as <name>_b64 (base64url).
// The backend EncodedPayloadMiddleware decodes both transparently, so the
// FastAPI endpoints keep receiving exactly the JSON / params they do today.
// ─────────────────────────────────────────────────────────────────────────────

export const API_BASE = process.env.REACT_APP_BACKEND || "http://localhost:8000";
const ENC_HEADER = "X-Payload-Encoding";
const GZIP_THRESHOLD = 2048; // bytes; compress larger bodies when the browser supports it

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("konet_token")}`,
});

// ── Encoding helpers ────────────────────────────────────────────────────────
function bytesToB64(bytes) {
  // chunked so large specs don't blow the call stack in String.fromCharCode
  let bin = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

function toB64Url(str) {
  return bytesToB64(new TextEncoder().encode(str))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

async function gzipBytes(bytes) {
  const stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream("gzip"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function encodeBody(obj) {
  const raw = new TextEncoder().encode(JSON.stringify(obj));
  if (typeof CompressionStream !== "undefined" && raw.length > GZIP_THRESHOLD) {
    return {
      encoding: "gzip+b64",
      body: JSON.stringify({ payload: bytesToB64(await gzipBytes(raw)) }),
    };
  }
  return { encoding: "b64", body: JSON.stringify({ payload: bytesToB64(raw) }) };
}

// ── Error handling (WAF-aware) ──────────────────────────────────────────────
export class ApiError extends Error {
  constructor(message, { status, wafTrackingId } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.wafTrackingId = wafTrackingId || null;
    this.isWafBlock = !!wafTrackingId || message.startsWith("Request blocked by the firewall");
  }
}

async function toApiError(res, fallbackMsg) {
  const contentType = res.headers.get("content-type") || "";
  const text = await res.text().catch(() => "");

  // Front Door WAF returns an HTML block page — never show raw HTML to the user
  if (contentType.includes("text/html") || /Web Application Firewall/i.test(text)) {
    const m = text.match(/\d{8}T\d{6}Z-[A-Za-z0-9]+/);
    return new ApiError(
      `Request blocked by the firewall${m ? ` (ref: ${m[0]})` : ""}. Please retry, or share the reference with support.`,
      { status: res.status, wafTrackingId: m?.[0] }
    );
  }

  let detail = null;
  try { detail = JSON.parse(text)?.detail ?? null; } catch { /* not JSON */ }
  if (detail && typeof detail !== "string") detail = JSON.stringify(detail);
  return new ApiError(detail || `${fallbackMsg} (${res.status})`, { status: res.status });
}

// ── Low-level request helpers ───────────────────────────────────────────────
export async function postEncoded(path, payload, fallbackMsg = "Request failed") {
  const { encoding, body } = await encodeBody(payload);
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", [ENC_HEADER]: encoding, ...authHeaders() },
    body,
  });
  if (!res.ok) throw await toApiError(res, fallbackMsg);
  return res.json();
}

async function getJson(path, fallbackMsg = "Request failed", encodedParams = false) {
  const headers = { ...authHeaders() };
  if (encodedParams) headers[ENC_HEADER] = "b64";
  const res = await fetch(`${API_BASE}${path}`, { headers });
  if (!res.ok) throw await toApiError(res, fallbackMsg);
  return res.json();
}

// ── Dashboard API ───────────────────────────────────────────────────────────

/** Generate a dashboard. businessContext is free text → sent base64url-encoded. */
export async function fetchDashboardSpec(sessionId, refresh = false, businessContext = "") {
  const qs = new URLSearchParams({ refresh: String(refresh) });
  const ctx = businessContext?.trim();
  if (ctx) qs.set("business_context_b64", toB64Url(ctx));
  return getJson(
    `/rag/dashboard/generate/${encodeURIComponent(sessionId)}?${qs}`,
    "Dashboard generation failed",
    !!ctx
  );
}

/** Save a version. Never throws — a failed save must not break generation/edit. */
export async function saveDashboardVersion(sessionId, spec, label) {
  try {
    await postEncoded(
      "/rag/dashboard/versions/save",
      { session_id: sessionId, spec, version_label: label },
      "Version save failed"
    );
    return true;
  } catch (e) {
    console.warn("Version save failed:", e.message, e.wafTrackingId || "");
    return false;
  }
}

export async function fetchVersionList(sessionId) {
  try {
    const d = await getJson(`/rag/dashboard/versions/${encodeURIComponent(sessionId)}`, "Could not load versions");
    return d.versions || [];
  } catch {
    return [];
  }
}

export async function fetchVersionSpec(sessionId, vNum) {
  return getJson(
    `/rag/dashboard/versions/${encodeURIComponent(sessionId)}/${encodeURIComponent(vNum)}`,
    "Version not found"
  );
}

/** Edit via prompt. Body has spec + prompt + sample rows → encoded. */
export async function editDashboard({ spec, sessionId, prompt, rows }) {
  return postEncoded(
    "/rag/dashboard/edit",
    {
      spec,
      session_id: sessionId,
      edit_prompt: prompt,
      refresh_insights: true,
      rows: (rows || []).slice(0, 50),
    },
    "Dashboard edit failed"
  );
}

/** Re-run widget SQL with filters. Body contains raw SQL → must be encoded. */
export async function queryDashboard(sessionId, spec, filters) {
  // server only needs ids + SQL + filter defs — keep the request small
  const slim = {
    version: spec.version,
    filters: spec.filters,
    kpis: (spec.kpis || []).map(({ id, sql }) => ({ id, sql })),
    charts: (spec.charts || []).map(({ id, sql, type, xKey, yKeys }) => ({ id, sql, type, xKey, yKeys })),
    tables: (spec.tables || []).map(({ id, sql, columns }) => ({ id, sql, columns })),
  };
  return postEncoded(
    "/rag/dashboard/query",
    { session_id: sessionId, spec: slim, filters },
    "Filter failed"
  );
}